/**
 * Edit Format Tool Schema
 *
 * Defines the detailed parameters for the editFormat tool.
 * This schema covers all formatting operations:
 * - Column widths and row heights
 * - Cell formatting (fonts, colors, borders, alignment)
 * - Conditional formatting rules
 */
import { z } from "zod";
// ═══════════════════════════════════════════════════════════════════════════
// BUILDING BLOCKS
// ═══════════════════════════════════════════════════════════════════════════
/**
 * RGB color as [R, G, B] array
 * Each value 0-255
 */
const RgbColorSchema = z.tuple([
    z.number().int().min(0).max(255),
    z.number().int().min(0).max(255),
    z.number().int().min(0).max(255),
]).describe("RGB color as [R, G, B], e.g., [31, 73, 125] for Excel blue");
/**
 * Address object for specifying cell ranges
 * Uses 1-indexed columns and rows
 */
const AddressSchema = z.object({
    startColumn: z.number().int().min(1).describe("1-indexed start column (A=1, B=2, D=4)"),
    endColumn: z.number().int().min(1).describe("1-indexed end column"),
    startRow: z.number().int().min(1).describe("1-indexed start row"),
    endRow: z.number().int().min(1).describe("1-indexed end row"),
});
/**
 * Border style specification
 */
const BorderSchema = z.object({
    clear: z.boolean().optional().describe("If true, removes this border"),
    color: RgbColorSchema.optional().describe("Border color as RGB"),
    style: z.enum(["Solid", "Dashed", "Dotted", "Double"]).optional().describe("Border line style"),
});
/**
 * Cell fill (background) specification
 */
const CellFillSchema = z.object({
    clear: z.boolean().optional().describe("If true, removes fill (makes transparent)"),
    color: RgbColorSchema.optional().describe("Fill color as RGB"),
});
// ═══════════════════════════════════════════════════════════════════════════
// ARRAY ITEM SCHEMAS
// ═══════════════════════════════════════════════════════════════════════════
/**
 * Column width specification
 * IMPORTANT: Width is in PIXELS, not Excel character units!
 */
const ColumnWidthSchema = z.object({
    column: z.number().int().min(1).describe("1-indexed column number (A=1, D=4)"),
    width: z.number().int().min(0).describe("Width in PIXELS (not character units). Common: 80-200"),
    allow_expand: z.boolean().optional().describe("If true, column auto-expands if text is wider"),
});
/**
 * Row height specification
 * IMPORTANT: Height is in PIXELS, not points!
 */
const RowHeightSchema = z.object({
    row: z.number().int().min(1).describe("1-indexed row number"),
    height: z.number().int().min(0).describe("Height in PIXELS (not points). Standard row ~20px, header ~25-30px"),
});
/**
 * Custom range formatting - applies formatting to one or more ranges
 * This is the main formatting specification
 */
const CustomRangeFormattingSchema = z.object({
    // Target ranges - multiple ranges can receive identical formatting
    addresses: z.array(AddressSchema).min(1).describe("One or more ranges to apply identical formatting. Multiple ranges = one operation."),
    // === BORDERS ===
    leftBorder: BorderSchema.optional(),
    rightBorder: BorderSchema.optional(),
    topBorder: BorderSchema.optional(),
    bottomBorder: BorderSchema.optional(),
    // === FILL ===
    cellFill: CellFillSchema.optional(),
    // === FONT ===
    fontName: z.string().optional().describe("Font family: 'Arial', 'Calibri', 'Times New Roman'"),
    fontSize: z.number().optional().describe("Font size in points (e.g., 10, 11, 12, 14)"),
    fontColor: RgbColorSchema.optional().describe("Font color as RGB"),
    bold: z.boolean().optional().describe("Bold text"),
    italic: z.boolean().optional().describe("Italic text"),
    underline: z.enum(["Single", "Double", "SingleAccounting", "DoubleAccounting", "None"]).optional()
        .describe("Underline style. 'None' removes underline."),
    // === LAYOUT ===
    alignment: z.enum(["Left", "Center", "Right", "Center Across Selection"]).optional()
        .describe("Horizontal text alignment"),
    indentLevel: z.number().int().min(0).optional().describe("Indent level (0 = no indent)"),
    numberFormat: z.string().optional().describe("Excel format string: '_($* #,##0_)' for currency, '0.00%' for percent, '#,##0' for number"),
    // === PRESET SHORTCUT ===
    cellPreset: z.enum(["input", "error_check", "comment"]).optional().describe("Quick preset: 'input' = light yellow, 'error_check' = light gray, 'comment' = 10pt italic"),
});
/**
 * Conditional formatting rule
 * Applies dynamic formatting based on cell values
 */
const ConditionalFormattingSchema = z.object({
    address: z.string().describe("Range in A1 notation: 'B2:B10', 'Sheet1!A1:D20'"),
    formatType: z.enum(["cellValue", "colorScale", "top10", "aboveAverage", "expression"])
        .describe("Type of conditional format rule"),
    // For cellValue type
    operator: z.enum([
        "greater", "less", "equal", "notEqual",
        "between", "notBetween", "greaterEqual", "lessEqual"
    ]).optional().describe("Comparison operator (required for cellValue type)"),
    // Values for comparison
    value1: z.union([
        z.number(),
        z.string(),
        z.array(z.number()), // For colorScale points
    ]).optional().describe("First comparison value, or array of scale points for colorScale"),
    value2: z.union([z.number(), z.string()]).optional()
        .describe("Second value (for 'between'/'notBetween', or 'percent' for top10)"),
    // Format to apply when condition is met
    format: z.object({
        backgroundColor: RgbColorSchema.optional(),
        fontColor: RgbColorSchema.optional(),
    }).optional().describe("Formatting to apply when condition is true"),
    // For colorScale type
    minColor: RgbColorSchema.optional().describe("Color for minimum value (colorScale)"),
    midColor: RgbColorSchema.optional().describe("Color for middle value (colorScale, optional)"),
    maxColor: RgbColorSchema.optional().describe("Color for maximum value (colorScale)"),
});
// ═══════════════════════════════════════════════════════════════════════════
// COPY FORMATTING SCHEMA
// ═══════════════════════════════════════════════════════════════════════════
/**
 * Copy formatting from a source range to one or more destination ranges.
 * All destination ranges must have the same dimensions as the source.
 * Useful for extending tables with consistent formatting.
 */
const CopyFormattingSourceSchema = z.object({
    startRow: z.number().int().min(1).describe('Source start row (1-indexed)'),
    endRow: z.number().int().min(1).describe('Source end row (1-indexed)'),
    startCol: z.number().int().min(1).describe('Source start column (1-indexed, A=1)'),
    endCol: z.number().int().min(1).describe('Source end column (1-indexed)'),
    sheetName: z.string().optional().describe('Source sheet name (defaults to current sheet)'),
});
const CopyFormattingDestSchema = z.object({
    startRow: z.number().int().min(1).describe('Destination start row (1-indexed)'),
    endRow: z.number().int().min(1).describe('Destination end row (1-indexed)'),
    startCol: z.number().int().min(1).describe('Destination start column (1-indexed)'),
    endCol: z.number().int().min(1).describe('Destination end column (1-indexed)'),
    sheetName: z.string().optional().describe('Destination sheet name (defaults to current sheet)'),
});
const CopyFormattingSchema = z.object({
    source: CopyFormattingSourceSchema.describe('Source range to copy formatting from'),
    destinations: z.array(CopyFormattingDestSchema).min(1).describe('Destination ranges (must have same dimensions as source)'),
});
// ═══════════════════════════════════════════════════════════════════════════
// MAIN TOOL SCHEMA
// ═══════════════════════════════════════════════════════════════════════════
export const EditFormatToolParamsSchema = z.object({
    uiDescription: z.string()
        .describe("Brief description (max ~15 words) of what this tool call is attempting."),
    // NOTE: sessionId is NOT in the schema - it's set via setSessionId() by the parent agent
    // This prevents the LLM from having to guess/make up session IDs
    // Target range for the overall operation
    targetRange: z.string().describe("Overall range being formatted in 'Sheet!Range' notation: 'Sheet1!A1:D10'"),
    // Dimension adjustments
    columnWidths: z.array(ColumnWidthSchema).optional().describe("Column width specifications. Widths are in PIXELS."),
    rowHeights: z.array(RowHeightSchema).optional().describe("Row height specifications. Heights are in PIXELS."),
    // Main formatting
    customRangeFormatting: z.array(CustomRangeFormattingSchema).optional().describe("Formatting runs - each applies a set of formats to one or more ranges"),
    // Conditional formatting
    conditionalFormatting: z.array(ConditionalFormattingSchema).optional().describe("Conditional formatting rules based on cell values"),
    // Clear conditional formatting
    clearConditionalFormatting: z.union([
        z.boolean(),
        z.array(z.string()),
    ]).optional().describe("true = clear all in targetRange, or array of specific ranges to clear"),
    // Copy formatting from source to destinations
    copyFormatting: CopyFormattingSchema.optional().describe("Copy all formatting from source range to destination ranges (must have same dimensions)"),
});
export { EditFormatToolParamsSchema as EditFormatParamsSchema };
